import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ============ LOGIN COMPONENT ============
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  return (
    <div className="login-container">
      <div className="logo-bg">
        <img src={`${process.env.PUBLIC_URL}/PPS/Polish_20260311_020010139.png`} alt="" onError={(e) => e.target.style.display = 'none'} />
      </div>
      <div className="login-box">
        <div className="login-logo">
          <img src={`${process.env.PUBLIC_URL}/PPS/Polish_20260311_020010139.png`} alt="PPS Logo" onError={(e) => e.target.style.display = 'none'} />
          <h1>Paradox Payroll</h1>
          <p>HR Agent Platform</p>
        </div>
        {error && <p style={{ color: '#dc3545', marginBottom: '20px', textAlign: 'center' }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Login</button>
        </form>
      </div>
    </div>
  );
}

// ============ DASHBOARD COMPONENT ============
function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState({ total: 0, pending: 0, inTraining: 0, active: 0, suspended: 0, terminated: 0 });
  const [applicants, setApplicants] = useState([]);
  const [activeTab, setActiveTab] = useState('applicants');
  const [showProfile, setShowProfile] = useState(null);
  const [masterLink, setMasterLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(null);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showDocEditModal, setShowDocEditModal] = useState(false);
  const [docToEdit, setDocToEdit] = useState(null);
  const [bulkFolderId, setBulkFolderId] = useState('');
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [showFillModal, setShowFillModal] = useState(false);
  const [fillDoc, setFillDoc] = useState(null);
  const [fillFields, setFillFields] = useState([]);
  const [fillValues, setFillValues] = useState({});
  const [isFilling, setIsFilling] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docForm, setDocForm] = useState({ category: 'General', description: '', agentId: '', displayName: '', employeeName: '', folderId: '' });
  const [docError, setDocError] = useState('');
  const [docSelected, setDocSelected] = useState([]);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('HR Documents');
  const [emailMessage, setEmailMessage] = useState('Please review the attached HR documents.');
  const [emailExpiresDays, setEmailExpiresDays] = useState(7);

  useEffect(() => {
    fetchStats();
    fetchApplicants();
    if (user.role === 'owner') {
      fetchMasterLink();
    }
  }, [activeTab, user.role]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchApplicants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/applicants`, { headers: { Authorization: `Bearer ${token}` } });
      setApplicants(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchMasterLink = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/links/master`, { headers: { Authorization: `Bearer ${token}` } });
      setMasterLink(res.data?.link || null);
    } catch (err) { console.error(err); }
  };

  const generateMasterLink = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/links/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMasterLink(res.data.link);
      setShowLinkModal(true);
    } catch (err) { console.error(err); }
  };

  const generateDocLink = async (applicantId, docType) => {
    try {
      const baseUrl = window.location.origin;
      const docLink = `${baseUrl}/document/${applicantId}/${docType}`;
      setShowDocModal({ link: docLink, docType });
    } catch (err) { console.error(err); }
  };

  const fetchDocs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedFolderId) params.folder_id = selectedFolderId;
      if (docSearch) params.search = docSearch;
      const res = await axios.get(`${API_URL}/hr-docs`, { headers: { Authorization: `Bearer ${token}` }, params });
      setDocs(res.data || []);
    } catch (err) {
      console.error(err);
      setDocError('Failed to load documents');
    }
  }, [selectedFolderId, docSearch]);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/hr-docs/folders`, { headers: { Authorization: `Bearer ${token}` } });
      setFolders(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openDocsModal = async () => {
    setDocError('');
    setShowDocsModal(true);
    await fetchFolders();
    await fetchDocs();
  };

  useEffect(() => {
    if (showDocsModal) {
      fetchDocs();
    }
  }, [showDocsModal, fetchDocs]);

  const openEmailModal = async () => {
    setDocError('');
    setSelectedFolderId('');
    setDocSearch('');
    setShowEmailModal(true);
    await fetchDocs();
  };

  const handleDocUpload = async () => {
    if (!docFile) {
      setDocError('Select a file to upload');
      return;
    }
    try {
      setDocError('');
      const token = localStorage.getItem('token');
      const metaRes = await axios.post(`${API_URL}/hr-docs`, {
        filename: docFile.name,
        category: docForm.category || 'General',
        description: docForm.description || '',
        agent_id: docForm.agentId || null,
        display_name: docForm.displayName || '',
        employee_name: docForm.employeeName || '',
        folder_id: docForm.folderId || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      const doc = metaRes.data;
      const uploadRes = await fetch(`${API_URL}/hr-docs/${doc.id}/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}` ,
          'Content-Type': docFile.type || 'application/octet-stream'
        },
        body: docFile
      });
      if (!uploadRes.ok) {
        let errorMessage = 'Upload failed';
        try {
          const payload = await uploadRes.json();
          errorMessage = payload?.error || payload?.message || errorMessage;
        } catch (parseErr) {
          // ignore parse errors
        }
        throw new Error(errorMessage);
      }

      setDocFile(null);
      setDocForm({ category: 'General', description: '', agentId: '', displayName: '', employeeName: '', folderId: '' });
      await fetchDocs();
    } catch (err) {
      console.error(err);
      setDocError(err?.message || 'Upload failed');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/hr-docs/folders`,
        { name: newFolderName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewFolderName('');
      setShowFolderModal(false);
      await fetchFolders();
    } catch (err) {
      console.error(err);
      setDocError('Failed to create folder');
    }
  };

  const openEditDoc = (doc) => {
    setDocToEdit(doc);
    setShowDocEditModal(true);
  };

  const saveDocEdit = async () => {
    if (!docToEdit) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/hr-docs/${docToEdit.id}`,
        {
          display_name: docToEdit.display_name || '',
          employee_name: docToEdit.employee_name || '',
          description: docToEdit.description || '',
          category: docToEdit.category || 'General',
          folder_id: docToEdit.folder_id || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDocEditModal(false);
      setDocToEdit(null);
      await fetchDocs();
    } catch (err) {
      console.error(err);
      setDocError('Failed to update document');
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/hr-docs/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchDocs();
    } catch (err) {
      console.error(err);
      setDocError('Failed to delete document');
    }
  };

  const moveSelectedDocs = async () => {
    if (docSelected.length === 0) {
      setDocError('Select at least one document');
      return;
    }
    if (!bulkFolderId) {
      setDocError('Choose a folder to move into');
      return;
    }
    try {
      setIsBulkMoving(true);
      const token = localStorage.getItem('token');
      await Promise.all(
        docSelected.map((id) =>
          axios.patch(
            `${API_URL}/hr-docs/${id}`,
            { folder_id: bulkFolderId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setDocSelected([]);
      setBulkFolderId('');
      await fetchDocs();
    } catch (err) {
      console.error(err);
      setDocError('Failed to move documents');
    } finally {
      setIsBulkMoving(false);
    }
  };

  const openFillDoc = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/hr-docs/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error('Unable to load document');
      }
      const buffer = await res.arrayBuffer();
      const pdfDoc = await PDFDocument.load(buffer);
      const form = pdfDoc.getForm();
      const fields = form.getFields().map((field) => ({ name: field.getName() }));
      const initialValues = {};
      fields.forEach((f) => { initialValues[f.name] = ''; });
      setFillDoc({ ...doc, pdfDoc });
      setFillFields(fields);
      setFillValues(initialValues);
      setShowFillModal(true);
    } catch (err) {
      console.error(err);
      setDocError('Failed to open document for filling');
    }
  };

  const handleFillChange = (name, value) => {
    setFillValues((prev) => ({ ...prev, [name]: value }));
  };

  const saveFilledDoc = async () => {
    if (!fillDoc?.pdfDoc) return;
    try {
      setIsFilling(true);
      const pdfDoc = fillDoc.pdfDoc;
      const form = pdfDoc.getForm();
      form.getFields().forEach((field) => {
        const name = field.getName();
        const value = fillValues[name];
        if (typeof field.setText === 'function') {
          field.setText(value || '');
        } else if (typeof field.check === 'function') {
          if (value) field.check();
          else field.uncheck();
        } else if (typeof field.select === 'function') {
          if (value) field.select(value);
        }
      });

      const bytes = await pdfDoc.save();
      const token = localStorage.getItem('token');
      const uploadRes = await fetch(`${API_URL}/hr-docs/${fillDoc.id}/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/pdf'
        },
        body: bytes
      });
      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }
      setShowFillModal(false);
      setFillDoc(null);
      setFillFields([]);
      setFillValues({});
      await fetchDocs();
    } catch (err) {
      console.error(err);
      setDocError('Failed to save filled document');
    } finally {
      setIsFilling(false);
    }
  };

  const toggleDocSelected = (id) => {
    setDocSelected((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
  };

  const sendDocs = async () => {
    if (!emailTo) {
      setDocError('Recipient email is required');
      return;
    }
    if (docSelected.length === 0) {
      setDocError('Select at least one document to send');
      return;
    }
    try {
      setDocError('');
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/hr-docs/send`, {
        to: emailTo,
        subject: emailSubject,
        message: emailMessage,
        document_ids: docSelected,
        expires_in_days: Number(emailExpiresDays) || 7
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDocSelected([]);
      setEmailTo('');
      setEmailSubject('HR Documents');
      setEmailMessage('Please review the attached HR documents.');
      setEmailExpiresDays(7);
      setDocError('Email sent');
    } catch (err) {
      console.error(err);
      setDocError(err.response?.data?.error || 'Email send failed');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING': return 'status-badge status-pending';
      case 'IN TRAINING': return 'status-badge status-in-training';
      case 'ACTIVE': return 'status-badge status-active';
      case 'SUSPENDED': return 'status-badge status-suspended';
      case 'TERMINATED': return 'status-badge status-terminated';
      default: return 'status-badge';
    }
  };

  const applicantStatuses = ['PENDING', 'IN TRAINING'];
  const employeeStatuses = ['ACTIVE', 'SUSPENDED'];
  const terminatedStatuses = ['TERMINATED', 'DENIED'];
  const filteredApplicants = activeTab === 'applicants'
    ? applicants.filter((a) => applicantStatuses.includes(a.status))
    : activeTab === 'employees'
      ? applicants.filter((a) => employeeStatuses.includes(a.status))
      : applicants.filter((a) => terminatedStatuses.includes(a.status));

  return (
    <div className="app-container">
      <div className="logo-bg">
        <img src={`${process.env.PUBLIC_URL}/PPS/Polish_20260311_020010139.png`} alt="" onError={(e) => e.target.style.display = 'none'} />
      </div>
      <div className="main-content">
        <div className="dashboard-header">
          <h1>HR Agent Dashboard</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {user.role === 'owner' && (
              <button onClick={generateMasterLink} className="btn btn-primary btn-small">Generate Onboarding Link</button>
            )}
            <button onClick={openDocsModal} className="btn btn-secondary btn-small">HR Documents</button>
            <button onClick={openEmailModal} className="btn btn-secondary btn-small">Email Documents</button>
            <span style={{ color: '#aaaaaa' }}>Welcome, {user.name} ({user.role})</span>
            <button onClick={onLogout} className="btn btn-secondary btn-small">Logout</button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="number">{stats.total}</div>
            <div className="label">Total Applicants</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.pending}</div>
            <div className="label">Pending Review</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.inTraining}</div>
            <div className="label">In Training</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.active}</div>
            <div className="label">Active Agents</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.suspended}</div>
            <div className="label">Suspended</div>
          </div>
          <div className="stat-card">
            <div className="number">{stats.terminated}</div>
            <div className="label">Terminated</div>
          </div>
        </div>

        <div className="nav-bar">
          <button className={`nav-link ${activeTab === 'applicants' ? 'active' : ''}`} onClick={() => setActiveTab('applicants')}>Applicants</button>
          <button className={`nav-link ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => setActiveTab('employees')}>Employees</button>
          <button className={`nav-link ${activeTab === 'terminated' ? 'active' : ''}`} onClick={() => setActiveTab('terminated')}>Terminated</button>
        </div>

        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>PPS #</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Applied</th>
                  <th>Documents</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplicants.map(applicant => (
                  <tr key={applicant.id}>
                    <td>{applicant.preferred_name || applicant.full_legal_name}</td>
                    <td>{applicant.pps_number || '-'}</td>
                    <td>{applicant.email}</td>
                    <td>{applicant.source || 'Direct'}</td>
                    <td><span className={getStatusBadgeClass(applicant.status)}>{applicant.status}</span></td>
                    <td>{new Date(applicant.created_at).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => generateDocLink(applicant.id, 'nda')} className="btn btn-secondary btn-small" style={{ marginRight: '5px' }}>NDA</button>
                      <button onClick={() => generateDocLink(applicant.id, 'agreement')} className="btn btn-secondary btn-small">Agreement</button>
                    </td>
                    <td>
                      <button onClick={() => setShowProfile(applicant)} className="btn btn-primary btn-small">View</button>
                    </td>
                  </tr>
                ))}
                {filteredApplicants.length === 0 && (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: '#aaaaaa' }}>No applicants found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showProfile && (
          <AgentProfile 
            agent={showProfile} 
            onClose={() => setShowProfile(null)} 
            user={user}
            onUpdate={fetchApplicants}
          />
        )}

        {showLinkModal && masterLink && (
          <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Master Onboarding Link</h2>
                <button className="modal-close" onClick={() => setShowLinkModal(false)}>&times;</button>
              </div>
              <p style={{ color: '#aaaaaa', marginBottom: '15px' }}>Share this link across job platforms. Each submission creates a unique applicant ID with source tracking.</p>
              <div className="link-box">
                <input type="text" value={masterLink} readOnly />
              </div>
              <button 
                onClick={() => navigator.clipboard.writeText(masterLink)} 
                className="btn btn-primary" 
                style={{ marginTop: '15px', width: '100%' }}
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        )}

        {showDocsModal && (
          <div className="modal-overlay" onClick={() => setShowDocsModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div className="modal-header">
                <h2>HR Document Library</h2>
                <button className="modal-close" onClick={() => setShowDocsModal(false)}>&times;</button>
              </div>

              {docError && <p style={{ color: '#dc3545', marginBottom: '12px' }}>{docError}</p>}

              <div className="profile-section">
                <h3>Folders & Search</h3>
                <div className="profile-grid">
                  <div className="profile-field">
                    <label>Folder</label>
                    <select value={selectedFolderId} onChange={(e) => setSelectedFolderId(e.target.value)}>
                      <option value="">All</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="profile-field">
                    <label>Search</label>
                    <input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search docs..." />
                  </div>
                  <div className="profile-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={() => setShowFolderModal(true)} className="btn btn-secondary btn-small">+ New Folder</button>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h3>Upload Document</h3>
                <div className="profile-grid">
                  <div className="profile-field">
                    <label>File</label>
                    <input type="file" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                  </div>
                  <div className="profile-field">
                    <label>Display Name</label>
                    <input value={docForm.displayName} onChange={(e) => setDocForm({ ...docForm, displayName: e.target.value })} placeholder="Title shown in list" />
                  </div>
                  <div className="profile-field">
                    <label>Category</label>
                    <input value={docForm.category} onChange={(e) => setDocForm({ ...docForm, category: e.target.value })} />
                  </div>
                  <div className="profile-field">
                    <label>Folder</label>
                    <select value={docForm.folderId} onChange={(e) => setDocForm({ ...docForm, folderId: e.target.value })}>
                      <option value="">None</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="profile-field">
                    <label>Agent ID (optional)</label>
                    <input value={docForm.agentId} onChange={(e) => setDocForm({ ...docForm, agentId: e.target.value })} />
                  </div>
                  <div className="profile-field">
                    <label>Employee Name</label>
                    <input value={docForm.employeeName} onChange={(e) => setDocForm({ ...docForm, employeeName: e.target.value })} />
                  </div>
                  <div className="profile-field">
                    <label>Description</label>
                    <input value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} />
                  </div>
                </div>
                <button onClick={handleDocUpload} className="btn btn-primary btn-small" style={{ marginTop: '10px' }}>Upload</button>
              </div>

              <div className="profile-section">
                <h3>All Documents</h3>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Title</th>
                        <th>Employee</th>
                        <th>Folder</th>
                        <th>Category</th>
                        <th>Size</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((doc) => (
                        <tr key={doc.id}>
                          <td>
                            <input type="checkbox" checked={docSelected.includes(doc.id)} onChange={() => toggleDocSelected(doc.id)} />
                          </td>
                          <td>{doc.display_name || doc.filename}</td>
                          <td>{doc.employee_name || '-'}</td>
                          <td>{doc.folder_name || '-'}</td>
                          <td>{doc.category || 'General'}</td>
                          <td>{doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '-'}</td>
                          <td>{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}</td>
                          <td>
                            <button onClick={() => openFillDoc(doc)} className="btn btn-secondary btn-small" style={{ marginRight: '6px' }}>Fill</button>
                            <button onClick={() => openEditDoc(doc)} className="btn btn-secondary btn-small" style={{ marginRight: '6px' }}>Edit</button>
                            <button onClick={() => deleteDoc(doc.id)} className="btn btn-secondary btn-small">Delete</button>
                          </td>
                        </tr>
                      ))}
                      {docs.length === 0 && (
                        <tr><td colSpan="8" style={{ textAlign: 'center', color: '#aaaaaa' }}>No documents yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="profile-section">
                <h3>Bulk Actions</h3>
                <div className="profile-grid">
                  <div className="profile-field">
                    <label>Move Selected To Folder</label>
                    <select value={bulkFolderId} onChange={(e) => setBulkFolderId(e.target.value)}>
                      <option value="">Select folder...</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="profile-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={moveSelectedDocs} className="btn btn-primary btn-small" disabled={isBulkMoving}>
                      {isBulkMoving ? 'Moving...' : 'Move Selected'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEmailModal && (
          <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <div className="modal-header">
                <h2>Email Documents</h2>
                <button className="modal-close" onClick={() => setShowEmailModal(false)}>&times;</button>
              </div>

              {docError && <p style={{ color: '#dc3545', marginBottom: '12px' }}>{docError}</p>}

              <div className="profile-section">
                <h3>Select Documents</h3>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Title</th>
                        <th>Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((doc) => (
                        <tr key={doc.id}>
                          <td>
                            <input type="checkbox" checked={docSelected.includes(doc.id)} onChange={() => toggleDocSelected(doc.id)} />
                          </td>
                          <td>{doc.display_name || doc.filename}</td>
                          <td>{doc.employee_name || '-'}</td>
                        </tr>
                      ))}
                      {docs.length === 0 && (
                        <tr><td colSpan="3" style={{ textAlign: 'center', color: '#aaaaaa' }}>No documents yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="profile-section">
                <h3>Email Details</h3>
                <div className="profile-grid">
                  <div className="profile-field">
                    <label>To</label>
                    <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@email.com" />
                  </div>
                  <div className="profile-field">
                    <label>Subject</label>
                    <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                  </div>
                  <div className="profile-field">
                    <label>Expires (days)</label>
                    <input type="number" min="1" value={emailExpiresDays} onChange={(e) => setEmailExpiresDays(e.target.value)} />
                  </div>
                  <div className="profile-field">
                    <label>Message</label>
                    <input value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} />
                  </div>
                </div>
                <button onClick={sendDocs} className="btn btn-primary btn-small" style={{ marginTop: '10px' }}>Send Email</button>
              </div>
            </div>
          </div>
        )}

        {showFolderModal && (
          <div className="modal-overlay" onClick={() => setShowFolderModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>New Folder</h2>
                <button className="modal-close" onClick={() => setShowFolderModal(false)}>&times;</button>
              </div>
              <div className="form-group">
                <label>Folder Name</label>
                <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} />
              </div>
              <button onClick={handleCreateFolder} className="btn btn-primary btn-small">Create Folder</button>
            </div>
          </div>
        )}

        {showDocEditModal && docToEdit && (
          <div className="modal-overlay" onClick={() => setShowDocEditModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h2>Edit Document</h2>
                <button className="modal-close" onClick={() => setShowDocEditModal(false)}>&times;</button>
              </div>
              <div className="profile-grid">
                <div className="profile-field">
                  <label>Display Name</label>
                  <input value={docToEdit.display_name || ''} onChange={(e) => setDocToEdit({ ...docToEdit, display_name: e.target.value })} />
                </div>
                <div className="profile-field">
                  <label>Employee Name</label>
                  <input value={docToEdit.employee_name || ''} onChange={(e) => setDocToEdit({ ...docToEdit, employee_name: e.target.value })} />
                </div>
                <div className="profile-field">
                  <label>Category</label>
                  <input value={docToEdit.category || ''} onChange={(e) => setDocToEdit({ ...docToEdit, category: e.target.value })} />
                </div>
                <div className="profile-field">
                  <label>Folder</label>
                  <select value={docToEdit.folder_id || ''} onChange={(e) => setDocToEdit({ ...docToEdit, folder_id: e.target.value })}>
                    <option value="">None</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </select>
                </div>
                <div className="profile-field">
                  <label>Description</label>
                  <input value={docToEdit.description || ''} onChange={(e) => setDocToEdit({ ...docToEdit, description: e.target.value })} />
                </div>
              </div>
              <button onClick={saveDocEdit} className="btn btn-primary btn-small">Save Changes</button>
            </div>
          </div>
        )}

        {showFillModal && fillDoc && (
          <div className="modal-overlay" onClick={() => setShowFillModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <div className="modal-header">
                <h2>Fill Document</h2>
                <button className="modal-close" onClick={() => setShowFillModal(false)}>&times;</button>
              </div>
              <div className="profile-section">
                <p style={{ color: '#aaaaaa', marginBottom: '10px' }}>
                  Fill the fields below and save to overwrite the document.
                </p>
                <div className="profile-grid">
                  {fillFields.map((field) => (
                    <div key={field.name} className="profile-field">
                      <label>{field.name}</label>
                      <input value={fillValues[field.name] || ''} onChange={(e) => handleFillChange(field.name, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={saveFilledDoc} className="btn btn-primary btn-small" disabled={isFilling}>
                {isFilling ? 'Saving...' : 'Save Filled Document'}
              </button>
            </div>
          </div>
        )}

        {showDocModal && (
          <div className="modal-overlay" onClick={() => setShowDocModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{showDocModal.docType === 'nda' ? 'NDA Document' : 'Agent Agreement'}</h2>
                <button className="modal-close" onClick={() => setShowDocModal(null)}>&times;</button>
              </div>
              <p style={{ color: '#aaaaaa', marginBottom: '15px' }}>Send this link to the agent to complete and sign the document.</p>
              <div className="link-box">
                <input type="text" value={showDocModal.link} readOnly />
              </div>
              <button 
                onClick={() => navigator.clipboard.writeText(showDocModal.link)} 
                className="btn btn-primary" 
                style={{ marginTop: '15px', width: '100%' }}
              >
                Copy Link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ AGENT PROFILE COMPONENT ============
function AgentProfile({ agent, onClose, user, onUpdate }) {
  const [training, setTraining] = useState([]);
  const [notes, setNotes] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newStatus, setNewStatus] = useState(agent.status);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const fetchTraining = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/training/${agent.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTraining(res.data);
    } catch (err) { console.error(err); }
  }, [agent.id]);

  const fetchNotes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/notes/${agent.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotes(res.data);
    } catch (err) { console.error(err); }
  }, [agent.id]);

  const fetchStatusHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/status-history/${agent.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setStatusHistory(res.data);
    } catch (err) { console.error(err); }
  }, [agent.id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/documents/${agent.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(res.data);
    } catch (err) { console.error(err); }
  }, [agent.id]);

  useEffect(() => {
    fetchTraining();
    fetchNotes();
    fetchStatusHistory();
    fetchDocuments();
  }, [fetchTraining, fetchNotes, fetchStatusHistory, fetchDocuments]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/notes`, { agent_id: agent.id, content: newNote }, { headers: { Authorization: `Bearer ${token}` } });
      setNewNote('');
      fetchNotes();
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/applicants/${agent.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setShowStatusModal(false);
      onUpdate();
      window.location.reload();
    } catch (err) { console.error(err); }
  };

  const addTrainingItem = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/training`, { agent_id: agent.id, module_name: 'New Module', completed: false }, { headers: { Authorization: `Bearer ${token}` } });
      fetchTraining();
    } catch (err) { console.error(err); }
  };

  const toggleTrainingComplete = async (item) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/training/${item.id}`, { completed: !item.completed }, { headers: { Authorization: `Bearer ${token}` } });
      fetchTraining();
    } catch (err) { console.error(err); }
  };

  const updateDocumentStatus = async (docId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/documents/${docId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      fetchDocuments();
    } catch (err) { console.error(err); }
  };

  const trainingProgress = training.length > 0 
    ? Math.round((training.filter(t => t.completed).length / training.length) * 100) 
    : 0;

  const getDocStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    return doc?.status || 'Pending';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <div className="modal-header">
          <h2>{agent.preferred_name || agent.full_legal_name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
          <span className={`status-badge status-${agent.status.toLowerCase().replace(' ', '-')}`}>{agent.status}</span>
          <span style={{ color: '#666', fontSize: '12px' }}>Source: {agent.source || 'Direct'}</span>
          <button onClick={() => setShowStatusModal(true)} className="btn btn-primary btn-small">Change Status</button>
        </div>

        {/* Documents Section */}
        <div className="profile-section">
          <h3>Document Status</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="training-item">
              <div>
                <strong>NDA</strong>
                <div style={{ fontSize: '12px', color: '#666' }}>Non-Disclosure Agreement</div>
              </div>
              <select 
                value={getDocStatus('NDA')} 
                onChange={(e) => {
                  const doc = documents.find(d => d.document_type === 'NDA');
                  if (doc) updateDocumentStatus(doc.id, e.target.value);
                }}
                style={{ padding: '5px 10px', background: '#2a2a2a', color: '#fff', border: '1px solid #3a3a3a', borderRadius: '4px' }}
              >
                <option value="Pending">Pending</option>
                <option value="Signed">Signed</option>
                <option value="Not Yet Issued">Not Yet Issued</option>
              </select>
            </div>
            <div className="training-item">
              <div>
                <strong>Agent Agreement</strong>
                <div style={{ fontSize: '12px', color: '#666' }}>Authorized Sales Agent Agreement</div>
              </div>
              <select 
                value={getDocStatus('Agent Agreement')} 
                onChange={(e) => {
                  const doc = documents.find(d => d.document_type === 'Agent Agreement');
                  if (doc) updateDocumentStatus(doc.id, e.target.value);
                }}
                style={{ padding: '5px 10px', background: '#2a2a2a', color: '#fff', border: '1px solid #3a3a3a', borderRadius: '4px' }}
              >
                <option value="Pending">Pending</option>
                <option value="Signed">Signed</option>
                <option value="Not Yet Issued">Not Yet Issued</option>
              </select>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h3>Personal Information</h3>
          <div className="profile-grid">
            <div className="profile-field"><label>Full Legal Name</label><span>{agent.full_legal_name}</span></div>
            <div className="profile-field"><label>Preferred Name</label><span>{agent.preferred_name || 'N/A'}</span></div>
            <div className="profile-field"><label>Email</label><span>{agent.email}</span></div>
            <div className="profile-field"><label>Primary Phone</label><span>{agent.primary_phone}</span></div>
            <div className="profile-field"><label>Secondary Phone</label><span>{agent.secondary_phone || 'N/A'}</span></div>
            <div className="profile-field"><label>Mailing Address</label><span>{agent.mailing_address || 'N/A'}</span></div>
          </div>
        </div>

        <div className="profile-section">
          <h3>Emergency Contact</h3>
          <div className="profile-grid">
            <div className="profile-field"><label>Name</label><span>{agent.emergency_contact_name || 'N/A'}</span></div>
            <div className="profile-field"><label>Relationship</label><span>{agent.emergency_contact_relationship || 'N/A'}</span></div>
            <div className="profile-field"><label>Phone</label><span>{agent.emergency_contact_phone || 'N/A'}</span></div>
          </div>
        </div>

        <div className="profile-section">
          <h3>Banking & Payment</h3>
          <div className="profile-grid">
            <div className="profile-field"><label>Payment Method</label><span>{agent.payment_method || 'N/A'}</span></div>
            {agent.payment_method === 'Direct Deposit' && (
              <>
                <div className="profile-field"><label>Bank Name</label><span>{agent.bank_name || 'N/A'}</span></div>
                <div className="profile-field"><label>Account Holder</label><span>{agent.account_holder_name || 'N/A'}</span></div>
              </>
            )}
            {agent.payment_method === 'Digital Payment' && (
              <div className="profile-field"><label>{agent.digital_platform}</label><span>{agent.digital_handle || 'N/A'}</span></div>
            )}
          </div>
        </div>

        <div className="profile-section">
          <h3>Identification</h3>
          <div className="profile-grid">
            <div className="profile-field"><label>ID Type</label><span>{agent.id_type || 'N/A'}</span></div>
            <div className="profile-field"><label>ID Number</label><span>{agent.id_number || 'N/A'}</span></div>
            <div className="profile-field"><label>Issuing State</label><span>{agent.id_issuing_state || 'N/A'}</span></div>
            <div className="profile-field"><label>Expiration</label><span>{agent.id_expiration || 'N/A'}</span></div>
          </div>
        </div>

        <div className="profile-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Training Progress</h3>
            <button onClick={addTrainingItem} className="btn btn-secondary btn-small">+ Add Module</button>
          </div>
          <div style={{ marginBottom: '10px' }}>Completion: {trainingProgress}%</div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${trainingProgress}%` }}></div></div>
          {training.map(item => (
            <div key={item.id} className={`training-item ${item.completed ? 'completed' : 'incomplete'}`}>
              <span>{item.module_name}</span>
              <button onClick={() => toggleTrainingComplete(item)} className="btn btn-small" style={{ background: item.completed ? '#28a745' : '#3a3a3a' }}>
                {item.completed ? 'Complete' : 'Incomplete'}
              </button>
            </div>
          ))}
        </div>

        <div className="profile-section">
          <h3>Notes</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." style={{ flex: 1 }} />
            <button onClick={handleAddNote} className="btn btn-primary btn-small">Add</button>
          </div>
          {notes.map(note => (
            <div key={note.id} className="note-item">
              <div className="note-header">
                <span>{note.author}</span>
                <span>{new Date(note.created_at).toLocaleString()}</span>
              </div>
              <div className="note-content">{note.content}</div>
            </div>
          ))}
        </div>

        <div className="profile-section">
          <h3>Status History</h3>
          {statusHistory.map(history => (
            <div key={history.id} style={{ padding: '10px', borderBottom: '1px solid #2a2a2a' }}>
              <span style={{ color: '#aaaaaa' }}>{history.old_status || 'None'} → {history.new_status}</span>
              <span style={{ float: 'right', color: '#666' }}>{history.changed_by}</span>
              {history.notes && <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>{history.notes}</div>}
            </div>
          ))}
        </div>

        {showStatusModal && (
          <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: '20px' }}>Change Status</h2>
              <div className="form-group">
                <label>New Status</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  <option value="PENDING">PENDING</option>
                  <option value="IN TRAINING">IN TRAINING</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="SUSPENDED">SUSPENDED</option>
                  <option value="TERMINATED">TERMINATED</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={handleStatusChange} className="btn btn-primary">Save</button>
                <button onClick={() => setShowStatusModal(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ DOCUMENT SIGNING PAGE ============
function DocumentSigningPage({ applicantId, docType }) {
  const [applicant, setApplicant] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signature, setSignature] = useState('');

  const fetchApplicant = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/applicants/${applicantId}`);
      setApplicant(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [applicantId]);

  useEffect(() => {
    fetchApplicant();
  }, [fetchApplicant]);

  const handleSign = async () => {
    try {
      const token = localStorage.getItem('token');
      // Update document status to signed
      const docsRes = await axios.get(`${API_URL}/documents/${applicantId}`, { headers: { Authorization: `Bearer ${token}` } });
      const docs = docsRes.data;
      const targetDoc = docs.find(d => d.document_type === (docType === 'nda' ? 'NDA' : 'Agent Agreement'));
      
      if (targetDoc) {
        await axios.patch(`${API_URL}/documents/${targetDoc.id}`, { status: 'Signed' }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      // Add note about signing
      await axios.post(`${API_URL}/notes`, { 
        agent_id: applicantId, 
        content: `${docType === 'nda' ? 'NDA' : 'Agent Agreement'} signed electronically. Signature: ${signature}` 
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setSigned(true);
    } catch (err) {
      console.error(err);
      alert('Error signing document');
    }
  };

  if (!applicant) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (signed) {
    return (
      <div className="application-container">
        <div className="application-form" style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#28a745', marginBottom: '20px' }}>Document Signed!</h1>
          <p>Thank you for signing the {docType === 'nda' ? 'NDA' : 'Agent Agreement'}.</p>
          <p style={{ marginTop: '20px', color: '#aaaaaa' }}>Your signature has been recorded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-container">
      <div className="logo-bg">
        <img src={`${process.env.PUBLIC_URL}/PPS/Polish_20260311_020010139.png`} alt="" onError={(e) => e.target.style.display = 'none'} />
      </div>
      <div className="application-form">
        <div className="application-header">
          <h1>Paradox Payroll</h1>
          <h2>{docType === 'nda' ? 'Non-Disclosure Agreement' : 'Authorized Sales Agent Agreement'}</h2>
          <p>{applicant.preferred_name || applicant.full_legal_name}</p>
        </div>

        <div style={{ background: '#1A1A1A', padding: '20px', borderRadius: '8px', marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}>
          <h3 style={{ color: '#CC0000', marginBottom: '15px' }}>NON-DISCLOSURE AGREEMENT</h3>
          <p style={{ color: '#aaaaaa', fontSize: '14px', lineHeight: '1.6' }}>
            This Non-Disclosure Agreement ("Agreement") is entered into by and between Paradox Payroll Systems ("Company") and the undersigned individual ("Agent").<br/><br/>
            <strong>1. CONFIDENTIAL INFORMATION</strong><br/>
            Agent acknowledges that they may have access to confidential information including but not limited to: business strategies, customer data, pricing information, and proprietary processes.<br/><br/>
            <strong>2. OBLIGATIONS</strong><br/>
            Agent agrees to hold all confidential information in strict confidence and not to disclose such information to any third party without prior written consent.<br/><br/>
            <strong>3. TERM</strong><br/>
            This Agreement shall remain in effect for a period of two (2) years from the date of signing.<br/><br/>
            <strong>4. GOVERNING LAW</strong><br/>
            This Agreement shall be governed by the laws of the State of Ohio.
          </p>
        </div>

        <div className="form-group">
          <label>Electronic Signature (Type your full legal name)</label>
          <input 
            type="text" 
            value={signature} 
            onChange={(e) => setSignature(e.target.value)} 
            placeholder={applicant.full_legal_name}
            style={{ fontFamily: 'cursive', fontSize: '18px' }}
          />
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="checkbox" required />
            <span style={{ color: '#aaaaaa' }}>I have read and agree to the terms of this {docType === 'nda' ? 'NDA' : 'Agreement'}</span>
          </label>
        </div>

        <button 
          onClick={handleSign} 
          className="btn btn-primary" 
          style={{ width: '100%', marginTop: '20px' }}
          disabled={!signature}
        >
          Sign & Submit
        </button>
      </div>
    </div>
  );
}

// ============ APPLICATION FORM ============
function ApplicationForm({ source }) {
  const [formData, setFormData] = useState({
    legal_first_name: '',
    legal_middle_name: '',
    legal_last_name: '',
    legal_suffix: '',
    preferred_name: '',
    date_of_birth: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    email: '',
    primary_phone: '',
    secondary_phone: '',
    preferred_phone: 'primary',
    preferred_contact_method: '',
    preferred_contact_time: '',
    alternative_contact_name: '',
    alternative_contact_relationship: '',
    alternative_contact_phone: '',
    id_type: '',
    id_number: '',
    id_issuing_state: '',
    id_expiration: '',
    ssn_last_4: '',
    account_holder_name: '',
    bank_name: '',
    bank_address: '',
    routing_number: '',
    account_number: '',
    source: source || 'direct'
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acknowledged) {
      alert('Please acknowledge the statement before submitting.');
      return;
    }
    try {
      await axios.post(`${API_URL}/applicants`, formData);
      setSubmitted(true);
    } catch (err) {
      alert('Error submitting application');
    }
  };

  if (submitted) {
    return (
      <div className="application-container">
        <div className="application-form" style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#28a745', marginBottom: '20px' }}>Application Submitted!</h1>
          <p>Thank you for your interest in Paradox Payroll Systems. Your application has been received and is pending review.</p>
          <p style={{ marginTop: '20px', color: '#aaaaaa' }}>You will be contacted via email once your application has been reviewed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-container">
      <div className="logo-bg">
        <img src={`${process.env.PUBLIC_URL}/PPS/Polish_20260311_020010139.png`} alt="" onError={(e) => e.target.style.display = 'none'} />
      </div>
      <div className="application-form">
        <div className="application-header">
          <h1>Paradox Payroll</h1>
          <h2>Sales Agent Application</h2>
          <p>Join our team of authorized sales agents</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Personal Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input name="legal_first_name" value={formData.legal_first_name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Middle Name</label>
                <input name="legal_middle_name" value={formData.legal_middle_name} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Last Name *</label>
                <input name="legal_last_name" value={formData.legal_last_name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Suffix</label>
                <input name="legal_suffix" value={formData.legal_suffix} onChange={handleChange} placeholder="Jr., Sr., III" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Name</label>
                <input name="preferred_name" value={formData.preferred_name} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Contact</h2>
            <div className="form-group">
              <label>Street Address</label>
              <input name="address_street" value={formData.address_street} onChange={handleChange} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input name="address_city" value={formData.address_city} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>State</label>
                <input name="address_state" value={formData.address_state} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>ZIP</label>
                <input name="address_zip" value={formData.address_zip} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Mobile Number *</label>
                <input name="primary_phone" value={formData.primary_phone} onChange={handleChange} required />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <input
                    type="radio"
                    name="preferred_phone"
                    value="primary"
                    checked={formData.preferred_phone === 'primary'}
                    onChange={handleChange}
                  />
                  Preferred
                </label>
              </div>
              <div className="form-group">
                <label>Additional Number</label>
                <input name="secondary_phone" value={formData.secondary_phone} onChange={handleChange} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <input
                    type="radio"
                    name="preferred_phone"
                    value="secondary"
                    checked={formData.preferred_phone === 'secondary'}
                    onChange={handleChange}
                  />
                  Preferred
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Contact Method</label>
                <select name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="text">Text</option>
                  <option value="call">Call</option>
                </select>
              </div>
              <div className="form-group">
                <label>Best Time to Reach You</label>
                <select name="preferred_contact_time" value={formData.preferred_contact_time} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="morning">Morning</option>
                  <option value="midday">Midday</option>
                  <option value="evening">Evening</option>
                  <option value="overnight">Overnight</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Alternative Contact Name</label>
                <input name="alternative_contact_name" value={formData.alternative_contact_name} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <input name="alternative_contact_relationship" value={formData.alternative_contact_relationship} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Alternative Contact Number</label>
                <input name="alternative_contact_phone" value={formData.alternative_contact_phone} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Identification</h2>
            <div className="form-row">
              <div className="form-group">
                <label>ID Type</label>
                <select name="id_type" value={formData.id_type} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="Driver's License">Driver's License</option>
                  <option value="State ID">State ID</option>
                  <option value="Passport">Passport</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>ID Number</label>
                <input name="id_number" value={formData.id_number} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Issuing State/Country</label>
                <input name="id_issuing_state" value={formData.id_issuing_state} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Expiration Date</label>
                <input type="date" name="id_expiration" value={formData.id_expiration} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>SSN (Last 4 digits)</label>
                <input name="ssn_last_4" value={formData.ssn_last_4} onChange={handleChange} maxLength={4} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Payments</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Name as Appears on Account</label>
                <input name="account_holder_name" value={formData.account_holder_name} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input name="bank_name" value={formData.bank_name} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label>Bank Address</label>
              <input name="bank_address" value={formData.bank_address} onChange={handleChange} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Routing Number</label>
                <input name="routing_number" value={formData.routing_number} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input name="account_number" value={formData.account_number} onChange={handleChange} />
              </div>
            </div>
            <p style={{ color: '#aaaaaa', fontSize: '12px' }}>
              A statement from your bank may be requested if hired. Additional accounts may be added if hired.
            </p>
          </div>

          <div className="form-section">
            <h2>Acknowledgements</h2>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                required
              />
              <span style={{ color: '#aaaaaa' }}>
                I acknowledge that the information provided is accurate to the best of my knowledge.
              </span>
            </label>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '20px' }}>
            Submit Application
          </button>
        </form>
      </div>
    </div>
  );
}

// ============ MAIN APP ============
function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('login');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setView('dashboard');
    }
    
    // Check routing
    const path = window.location.pathname;
    
    // Document signing page
    if (path.startsWith('/document/')) {
      const parts = path.split('/document/')[1].split('/');
      const applicantId = parts[0];
      const docType = parts[1];
      setView({ type: 'document', applicantId, docType });
      return;
    }
    
    // Application form with source
    if (path.startsWith('/apply')) {
      const linkId = path.split('/apply/')[1];
      if (linkId) {
        setView({ type: 'apply', source: linkId });
      } else {
        setView('apply');
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setView('dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setView('login');
  };

  // Document signing page
  if (view && view.type === 'document') {
    return <DocumentSigningPage applicantId={view.applicantId} docType={view.docType} />;
  }

  // Application form
  if (view === 'apply' || (view && view.type === 'apply')) {
    const source = view && view.source ? view.source : null;
    return <ApplicationForm source={source} />;
  }

  // Login
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // Dashboard
  return <Dashboard user={user} onLogout={handleLogout} />;
}

export default App;
